import { useState } from 'react'
import { Alert, Button, Dropdown, Form, InputNumber, Modal, Select } from 'antd'
import { MoreOutlined, PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColumnsType } from 'antd/es/table'
import { DataTable } from '@kolab/dms-base/components/ui'
import { PageLayout } from '@kolab/dms-base/components/layout'
import { toast, getFriendlyErrorMessage } from '@kolab/dms-base/lib'
import {
  allocateResellerLicense,
  listResellerLicenses,
  setResellerLicenseStatus,
  updateResellerLicenseLimit,
} from '../api/resellerLicense.api'
import { pickDistributorLicenses, pickResellers } from '../api/lookups.api'
import { resellerLicenseKeys } from '../api/queryKeys'
import type { ResellerLicense, ResellerLicenseStatus } from '../api/types'
import { StatusBadge } from '../components/common/StatusBadge'
import { Can, P, usePermissionStore } from '../rbac'
import { useCursorList } from '../hooks/useCursorList'
import { CursorListStateView } from '../components/common/QueryStateView'


type Mode =
  | { kind: 'closed' }
  | { kind: 'allocate' }
  | { kind: 'edit-limit'; license: ResellerLicense }

export default function ResellerLicensesPage() {
  const [resellerFilter, setResellerFilter] = useState<string | undefined>()
  const queryClient = useQueryClient()

  const { items, isLoading, isError, error, refetch, isFetchingNextPage, hasNextPage, fetchNextPage } = useCursorList<ResellerLicense>({
    queryKey: resellerLicenseKeys.list({ pageSize: 20, resellerId: resellerFilter }),
    queryFn: (params) => listResellerLicenses(params),
    params: { resellerId: resellerFilter },
    pageSize: 20,
  })

  const { hasPermission } = usePermissionStore()

  const resellersQuery = useQuery({
    queryKey: ['lookups', 'resellers'],
    queryFn: () => pickResellers(),
    enabled: hasPermission(P.tenants.reseller.pick),
  })

  const parentPoolsQuery = useQuery({
    queryKey: ['lookups', 'distributor-licenses'],
    queryFn: () => pickDistributorLicenses(),
    enabled: hasPermission(P.licenses.distributor_pool.pick),
  })

  const [modal, setModal] = useState<Mode>({ kind: 'closed' })
  const [formError, setFormError] = useState<string | null>(null)

  function handleFormError(error: unknown, fallback: string) {
    if (error && typeof error === 'object' && 'status' in error) {
      const status = (error as { status?: number }).status
      if (status === 401 || status === 403 || (status && status >= 500)) return
    }
    setFormError(getFriendlyErrorMessage(error, fallback))
  }

  function openModal(mode: Mode) {
    setFormError(null)
    setModal(mode)
  }

  const allocateMut = useMutation({
    mutationFn: allocateResellerLicense,
    onSuccess: () => {
      toast.success('License allocated')
      queryClient.invalidateQueries({ queryKey: resellerLicenseKeys.all })
      setModal({ kind: 'closed' })
    },
    onError: (error: unknown) => handleFormError(error, 'Failed to allocate license'),
  })

  const limitMut = useMutation({
    mutationFn: ({ id, deviceLimit }: { id: string; deviceLimit: number }) =>
      updateResellerLicenseLimit(id, { deviceLimit }),
    onSuccess: () => {
      toast.success('Device limit updated')
      queryClient.invalidateQueries({ queryKey: resellerLicenseKeys.all })
      setModal({ kind: 'closed' })
    },
    onError: (error: unknown) => handleFormError(error, 'Failed to update device limit'),
  })

  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: ResellerLicenseStatus }) =>
      setResellerLicenseStatus(id, { status }),
    onSuccess: () => {
      toast.success('Status updated')
      queryClient.invalidateQueries({ queryKey: resellerLicenseKeys.all })
    },
    onError: (error: unknown) => {
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as { status?: number }).status
        if (status === 401 || status === 403 || (status && status >= 500)) return
      }
      toast.error(getFriendlyErrorMessage(error, 'Failed to update license status'))
    },
  })

  const resellerOptions = (resellersQuery.data?.items ?? []).map((r) => ({ label: r.label, value: r.id }))
  const poolOptions = (parentPoolsQuery.data?.items ?? []).map((p) => ({
    label: p.label,
    value: p.id,
  }))

  const columns: ColumnsType<ResellerLicense> = [
    { title: 'Sub-license', dataIndex: 'subLicenseKey', key: 'subLicenseKey' },
    {
      title: 'Reseller',
      dataIndex: 'resellerName',
      key: 'resellerName',
    },
    {
      title: 'Parent pool',
      dataIndex: 'distributorLicenseId',
      key: 'distributorLicenseId',
      render: (id: string) => parentPoolsQuery.data?.items?.find((p) => p.id === id)?.label ?? id,
    },
    { title: 'Type', dataIndex: 'licenseType', key: 'licenseType' },
    { title: 'Device limit', dataIndex: 'deviceLimit', key: 'deviceLimit', align: 'right' as const },
    { title: 'Expiry', dataIndex: 'expiryDate', key: 'expiryDate' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => <StatusBadge status={s} />,
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, record) => {
        const items: { key: string; label: string; onClick: () => void; danger?: boolean }[] = []
        if (hasPermission(P.licenses.reseller_allocation.update)) {
          items.push({
            key: 'edit',
            label: 'Update limit',
            onClick: () => openModal({ kind: 'edit-limit', license: record }),
          })
        }
        if (hasPermission(P.licenses.reseller_allocation.update)) {
          for (const status of ['ACTIVE', 'SUSPENDED'] as ResellerLicenseStatus[]) {
            items.push({
              key: status,
              label: `Set ${status}`,
              onClick: () => statusMut.mutate({ id: record.id, status }),
            })
          }
        }
        if (hasPermission(P.licenses.reseller_allocation.revoke)) {
          items.push({
            key: 'REVOKED',
            label: 'Set REVOKED',
            danger: true,
            onClick: () => statusMut.mutate({ id: record.id, status: 'REVOKED' }),
          })
        }

        if (items.length === 0) return null
        return (
          <Dropdown trigger={['click']} menu={{ items }} getPopupContainer={() => document.body}>
            <Button size="small" icon={<MoreOutlined />} />
          </Dropdown>
        )
      },
    },
  ]

  return (
    <PageLayout
      actions={
        <Can permission={P.licenses.reseller_allocation.allocate}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal({ kind: 'allocate' })}>
            Allocate
          </Button>
        </Can>
      }
      filters={
        <Select
          allowClear
          placeholder="Filter by reseller"
          style={{ minWidth: 220 }}
          options={resellerOptions}
          value={resellerFilter}
          onChange={(v) => setResellerFilter(v)}
        />
      }
    >
      <CursorListStateView isLoading={isLoading} isError={isError} error={error} refetch={refetch}>
        <DataTable<ResellerLicense>
          rowKey="id"
          columns={columns}
          dataSource={items}
          pagination={false}
          infiniteScroll={{ isFetchingNextPage, hasNextPage, fetchNextPage }}
        />
      </CursorListStateView>

      <AllocateModal
        mode={modal}
        resellerOptions={resellerOptions}
        poolOptions={poolOptions}
        onCancel={() => setModal({ kind: 'closed' })}
        onAllocate={(fields) => allocateMut.mutate(fields)}
        onUpdateLimit={(id, deviceLimit) => limitMut.mutate({ id, deviceLimit })}
        submitting={allocateMut.isPending || limitMut.isPending}
        error={formError}
        onClearError={() => setFormError(null)}
      />
    </PageLayout>
  )
}

function AllocateModal({
  mode,
  resellerOptions,
  poolOptions,
  onCancel,
  onAllocate,
  onUpdateLimit,
  submitting,
  error,
  onClearError,
}: {
  mode: Mode
  resellerOptions: { label: string; value: string }[]
  poolOptions: { label: string; value: string }[]
  onCancel: () => void
  onAllocate: (fields: { distributorLicenseId: string; resellerId: string; deviceLimit: number }) => void
  onUpdateLimit: (id: string, deviceLimit: number) => void
  submitting: boolean
  error: string | null
  onClearError: () => void
}) {
  const [form] = Form.useForm<{ distributorLicenseId: string; resellerId: string; deviceLimit: number }>()
  const isEditLimit = mode.kind === 'edit-limit'
  const initial =
    mode.kind === 'edit-limit'
      ? { deviceLimit: mode.license.deviceLimit }
      : { deviceLimit: 10 }

  return (
    <Modal
      open={mode.kind !== 'closed'}
      title={isEditLimit ? 'Update device limit' : 'Allocate license to reseller'}
      onCancel={onCancel}
      onOk={() => form.submit()}
      okButtonProps={{ loading: submitting }}
      destroyOnClose
    >
      {error && (
        <Alert type="error" message={error} closable onClose={onClearError} style={{ marginBottom: 16 }} />
      )}
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={initial}
        onFinish={(values) => {
          if (isEditLimit && mode.kind === 'edit-limit') {
            onUpdateLimit(mode.license.id, values.deviceLimit)
          } else {
            onAllocate(values)
          }
        }}
      >
        {!isEditLimit && (
          <>
            <Form.Item name="distributorLicenseId" label="Parent license pool" rules={[{ required: true }]}>
              <Select options={poolOptions} placeholder="Select a pool" />
            </Form.Item>
            <Form.Item name="resellerId" label="Reseller" rules={[{ required: true }]}>
              <Select options={resellerOptions} placeholder="Select a reseller" />
            </Form.Item>
          </>
        )}
        <Form.Item name="deviceLimit" label="Device limit" rules={[{ required: true, type: 'number', min: 1 }]}>
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export { ResellerLicensesPage }
